import { Cache, UpdatesConfig } from "@urql/exchange-graphcache";
import {
  Audience,
  AudienceForTenantDocument,
  AudienceForTenantQuery,
  AudienceForTenantQueryVariables,
  AudienceForUserDocument,
  AudienceForUserQuery,
  AudienceForUserQueryVariables,
  CommonAudienceFragmentDoc,
  ConfigurationAttribute,
  GroupPoliciesDocument,
  GroupPoliciesQuery,
  GroupPoliciesQueryVariables,
  GroupPolicyInput,
  Maybe,
  PoliciesDocument,
  PoliciesQuery,
  PoliciesQueryVariables,
  Policy,
  PolicyDeploymentScope,
  TeamsHooksForTenantDocument,
  ServiceNowHooksForTenantDocument,
  TeamsHooksForTenantQuery,
  TeamsHooksForTenantQueryVariables,
  ServiceNowHooksForTenantQuery,
  ServiceNowHooksForTenantQueryVariables,
  ParamArrayItem,
  Param2DArrayItem,
  UserGroupsForTenantQuery,
  ListUserGroupsForTenantDocument,
  UserGroup,
  UserGroupsForTenantQueryVariables,
  ListUserGroupsForTenantQuery,
  ListUserGroupsForTenantQueryVariables,
  UserGroupsForTenantDocument,
  AccountsListQuery,
  AccountsListDocument,
  AccountsListQueryVariables,
  CountAccountsQuery,
  CountAccountsDocument,
  TenantType,
  SoftwareSupportUpdatePath
} from "generated/graphql";
import { Scopes } from "routes/Manage/Policy/PolicyList";
import { MutationUpdateResolvers } from "generated/graphql.cache";
import { gql } from "urql";

const invalidateQueries = (cache: Cache, ...fieldNames: Array<string>) => {
  const key = "Query";
  fieldNames.forEach(fieldName => {
    cache
      .inspectFields(key)
      .filter(field => field.fieldName === fieldName)
      .forEach(field => {
        cache.invalidate(key, field.fieldName, field.arguments);
      });
  });
};

const invalidateDeviceParametersExtended = (
  cache: Cache,
  filters?: Maybe<{
    deviceModel?: Maybe<string>;
    deviceId?: Maybe<string>;
    siteIds?: string[];
    groupId?: Maybe<string>;
  }>
) => {
  // invalidate corresponding getDeviceParametersExtended queries
  // match by device model/siteIds/groupId/deviceId if they exist
  const { deviceModel, deviceId, siteIds, groupId } = filters ?? {};
  cache
    .inspectFields("Query")
    .filter(
      ({ fieldName, arguments: args }) =>
        fieldName === "getDeviceParametersExtended" &&
        (!deviceModel || args?.deviceModel === deviceModel) &&
        (!deviceId || args?.deviceId === deviceId) &&
        (!siteIds ||
          siteIds.length === 0 ||
          (args?.siteId && siteIds.includes(args.siteId as string))) &&
        (!groupId || args?.groupId === groupId)
    )
    .forEach(({ fieldKey }) => {
      cache.invalidate("Query", fieldKey);
    });
};

const Mutation: Partial<MutationUpdateResolvers> = {
  createPolicy: (result, args, cache) => {
    // Only do all this stuff if the result is successful
    if (!result.createPolicy) {
      return;
    }
    const newPolicy = args.policy as Policy;
    let formattedConfigs: ConfigurationAttribute[] = [];
    if (newPolicy.configurationAttributes) {
      // transform from input form to Policy form so it can write to cache
      const defaultConfig: ConfigurationAttribute = {
        __typename: "ConfigurationAttribute",
        name: "",
        currentValue: null,
        paramArray: null,
        param2DArray: null
      };
      const mappedConfigs = newPolicy.configurationAttributes.map(config => {
        const paramArray: Maybe<Maybe<ParamArrayItem>[]> =
          config?.paramArray?.map(
            item => item && { ...item, __typename: "ParamArrayItem" }
          ) || null;
        const param2DArray: Maybe<Maybe<Param2DArrayItem>[]> =
          config?.param2DArray?.map(
            item => item && { ...item, __typename: "Param2DArrayItem" }
          ) || null;
        const newConfig = {
          ...config,
          paramArray,
          param2DArray
        };
        return {
          ...defaultConfig,
          ...newConfig
        };
      });
      formattedConfigs = mappedConfigs;
    }
    let customSwSupportUpdatePath: Maybe<Maybe<
      SoftwareSupportUpdatePath
    >[]> = null;
    if (newPolicy.customSwSupportUpdatePath) {
      // transform from input form to Policy form so it can write to cache
      customSwSupportUpdatePath = newPolicy.customSwSupportUpdatePath.map(
        updatePath => {
          if (!updatePath) return null;

          return {
            ...updatePath,
            __typename: "SoftwareSupportUpdatePath"
          };
        }
      );
    }
    const { siteId, groupId, deviceModel, deviceId } = newPolicy;
    const entityId = siteId ?? groupId;
    const tenantId = args.tenantId as string;
    const scope = newPolicy.policyDeploymentScope;
    // get all entries in the cache for this tenant that are from the getPolicies query
    let relevantUpdates = [];
    const allGetPolicies = cache
      .inspectFields("Query")
      .filter(
        ({ fieldName, fieldKey }) =>
          fieldName.includes("getPolicies") && fieldKey.includes(tenantId)
      );
    // get most recent query since that one is definitely relevant for updating
    const mostRecent = allGetPolicies[allGetPolicies.length - 1];
    // push most recent into relevant updates
    relevantUpdates.push(mostRecent);
    // If the most recent was on the policy details page, we also need to look for any previous queries that came from a policy list
    const isPolicyDetails = mostRecent.fieldKey.includes(deviceModel!);
    if (scope && isPolicyDetails) {
      if (entityId) {
        const nextToUpdate = allGetPolicies.find(
          ({ fieldKey }) =>
            fieldKey.includes(entityId) && fieldKey.includes(scope)
        );
        if (nextToUpdate) {
          relevantUpdates.push(nextToUpdate);
        }
      } else {
        const nextToUpdate = allGetPolicies.find(({ fieldKey }) =>
          fieldKey.includes(scope)
        );
        if (nextToUpdate) {
          relevantUpdates.push(nextToUpdate);
        }
      }
      // else if we're on Policy List as most recent, we need to look for any previous ones from policy details
    } else if (!isPolicyDetails) {
      const nextToUpdate = allGetPolicies.find(({ fieldKey }) =>
        fieldKey.includes(deviceModel!)
      );
      if (nextToUpdate) {
        relevantUpdates.push(nextToUpdate);
      }
    }
    // Then for each relevant query in the cache, update that query to include the newly created one.
    relevantUpdates.forEach(getPolicies => {
      const variables: PoliciesQueryVariables = {
        tenantId
      };
      let hasDeviceModel = false;
      if (getPolicies.arguments) {
        hasDeviceModel = Object.keys(getPolicies.arguments).includes(
          "deviceModel"
        );
      }
      if (hasDeviceModel) {
        variables.deviceModel = getPolicies.arguments?.deviceModel as string;
      }
      if (!hasDeviceModel && siteId) {
        variables.siteId = siteId;
        variables.scope = PolicyDeploymentScope.Site;
      } else if (!hasDeviceModel && groupId) {
        variables.groupId = groupId;
        variables.scope = PolicyDeploymentScope.Group;
      } else if (!hasDeviceModel && !groupId && !siteId) {
        variables.scope = PolicyDeploymentScope.DeviceModel;
      }
      cache.updateQuery<PoliciesQuery, PoliciesQueryVariables>(
        {
          query: PoliciesDocument,
          variables
        },
        data => {
          if (!data || !data.getPolicies) return null;
          data.getPolicies.push({
            ...newPolicy,
            policyId: result.createPolicy as string,
            configurationAttributes: formattedConfigs,
            customSwSupportUpdatePath,
            __typename: "Policy"
          });
          return data;
        }
      );
    });
    invalidateDeviceParametersExtended(cache, {
      deviceModel,
      deviceId,
      groupId,
      siteIds: siteId ? [siteId] : []
    });
  },
  createPolicyCapabilities: (result, args, cache) => {
    // Only do all this stuff if the result is successful
    if (!result.createPolicyCapabilities) {
      return;
    }
    const newPolicy = args.input as GroupPolicyInput;
    let rules: {
      [key: string]: string;
    } = {
      site: "",
      group: "",
      user_group: "",
      model: "",
      device: "",
      tenantId: ""
    };

    newPolicy.rule?.and?.forEach(rule => {
      Object.keys(rules).forEach(key => {
        if (rule.equal?.key === key) {
          rules[key] = rule.equal.value!;
        }
      });
    });

    const scopeRules = {
      group: [{ equal: { key: "group", value: rules.group } }],
      site: [{ equal: { key: "site", value: rules.site } }],
      model: [{ equal: { key: "model", value: rules.model } }],
      user_group: [{ equal: { key: "user_group", value: rules.user_group } }],
      device: [{ equal: { key: "device", value: rules.device } }],
      unknown: []
    };

    const scopeRule =
      scopeRules[
        (result.createPolicyCapabilities?.type as Scopes) || "unknown"
      ];

    const variables: GroupPoliciesQueryVariables = {
      rules: {
        and: [
          {
            equal: {
              key: "tenantId",
              value: rules.tenantId
            }
          },
          ...scopeRule
        ]
      }
    };

    cache.updateQuery<GroupPoliciesQuery, GroupPoliciesQueryVariables>(
      {
        query: GroupPoliciesDocument,
        variables
      },
      data => {
        if (!data || !data.policiesCapabilities) return null;
        data.policiesCapabilities.push({
          hardwareModel: {
            id: rules.device,
            name: result.createPolicyCapabilities?.hardwareModel?.name || "",
            __typename: "HardwareModel"
          },
          settingsCount: result.createPolicyCapabilities?.settingsCount || 0,
          collectionId: result.createPolicyCapabilities?.collectionId || "",
          name: result.createPolicyCapabilities?.name || "",
          id: result.createPolicyCapabilities?.id || "",
          type: result.createPolicyCapabilities?.type || "",
          priority: result.createPolicyCapabilities?.priority || "0",
          __typename: "GroupPolicy"
        });
        return data;
      }
    );

    invalidateDeviceParametersExtended(cache, {
      deviceModel: rules.model,
      deviceId: rules.device,
      groupId: rules.group,
      siteIds: rules.site ? [rules.site] : []
    });

    cache.invalidate({
      __typename: "Query",
      id: "devicePolicyCapabilities"
    });
  },
  uninstallCertificate: (result, args, cache) => {
    if (result.uninstallCertificate?.success) {
      cache.invalidate({
        __typename: "CertificateResponse",
        deviceId: result.uninstallCertificate.deviceId!
      });
    }
  },
  updatePolicy: (result, args, cache) => {
    const { deviceModel, deviceId, groupId, siteId } = args.policy ?? {};
    invalidateDeviceParametersExtended(cache, {
      deviceModel,
      deviceId,
      groupId,
      siteIds: siteId ? [siteId] : []
    });
  },
  updatePolicyCapabilities: (result, args, cache) => {
    // if we are only updating the priority field, and we get a result.
    if (
      args.input?.id &&
      args.input?.priority &&
      Object.keys(args.input).length === 2 &&
      result?.updatePolicyCapabilities
    ) {
      // update result in queries
      const groupPolicyFragment = gql`
        fragment _ on GroupPolicy {
          id
          priority
        }
      `;
      const { id, priority } = result.updatePolicyCapabilities;
      cache.writeFragment(groupPolicyFragment, {
        id,
        priority
      });
      return;
    }

    // otherwise, invalidate cache for this GroupPolicy
    if (args.input?.id) {
      cache.invalidate({
        __typename: "GroupPolicy",
        id: args.input?.id
      });

      cache.invalidate({
        __typename: "Query",
        id: "devicePolicyCapabilities"
      });
    }
  },
  deletePolicies: (result, args, cache) => {
    const policyIdsToRemove = args.policyIds;
    if (policyIdsToRemove && policyIdsToRemove?.length > 0) {
      policyIdsToRemove.forEach(policyId => {
        cache.invalidate({
          __typename: "Policy",
          policyId
        });
      });
    }
    invalidateDeviceParametersExtended(cache);
  },
  deletePolicyById: (result, args, cache) => {
    if (args.policyId) {
      cache.invalidate({
        __typename: "Policy",
        policyId: args.policyId
      });
      invalidateDeviceParametersExtended(cache);
    }
  },
  deletePoliciesBySiteId: (result, args, cache) => {
    if (args.siteIds && args.siteIds.length) {
      cache.invalidate("Query", "getPolicies");
      invalidateDeviceParametersExtended(cache, {
        siteIds: args.siteIds.filter((id): id is string => !!id)
      });
    }
  },
  deletePoliciesCapabilities: (result, args, cache) => {
    const policyIdsToRemove = args.policyIds;
    if (policyIdsToRemove && policyIdsToRemove?.length > 0) {
      policyIdsToRemove.forEach(policyId => {
        cache.invalidate({
          __typename: "GroupPolicy",
          id: policyId
        });
      });
    }
    invalidateDeviceParametersExtended(cache);
  },
  disassociateWithResource: (result, args, cache) => {
    if (args.accessorId) {
      cache.invalidate({
        __typename: "User",
        user_id: args.accessorId
      });
    }
  },
  setDeviceSettingsWithXML: (result, args, cache) => {
    if (args.id) {
      cache.invalidate({
        __typename: "SettingXMLResponse",
        id: args.id
      });

      cache.invalidate({
        __typename: "SettingResponse",
        id: args.id
      });
    }
  },
  unregisterDevice: (result, args, cache) => {
    if (args.deviceId) {
      cache.invalidate({
        __typename: "Device",
        id: args.deviceId
      });
    }
  },
  rebootDevice: (result, args, cache) => {
    if (args.deviceId) {
      cache.invalidate({
        __typename: "Device",
        id: args.deviceId
      });
    }
  },
  updateProvisioningAccount: (result, args, cache) => {
    // in case of account creation (no uuid) we should update provisioning account query manually
    const uuid = args.account?.uuid;
    if (!uuid) {
      cache.invalidate({
        __typename: "Query",
        id: "getProvisioningAccounts"
      });
    }
  },
  upsertRoom: (result, args, cache) => {
    if (args.fields) {
      // in case of room creation (no id) we should invalidate getSite query
      if (!args.fields.id && args.fields.siteId) {
        cache.invalidate("Query", "getSite", { id: args.fields.siteId });
      }
    }
  },
  upsertSite: (result, args, cache) => {
    if (args.fields) {
      cache.invalidate({
        __typename: "Query",
        id: "countDevices"
      });
    }
  },
  deleteSites: (result, args, cache) => {
    args.ids?.forEach(siteId => {
      cache.invalidate({
        __typename: "Site",
        id: siteId
      });
    });
  },
  registerDevice: (result, args, cache) => {
    if (args.device && args.device.tenantId) {
      cache.invalidate("Query", "sites", { tenantId: args.device.tenantId });
    }
  },
  upsertDeviceSettings: (result, args, cache) => {
    if (args.id) {
      cache.invalidate({
        __typename: "SettingResponse",
        id: args.id
      });

      cache.invalidate({
        __typename: "SettingXMLResponse",
        id: args.id
      });
    }
  },
  mergeInternalTenants: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "countDevices"
    });
    cache.invalidate({
      __typename: "Query",
      id: "deviceAggregations"
    });
    cache.invalidate({
      __typename: "Query",
      id: "users"
    });
    const tenantId = args.mergeToTenantId;
    if (tenantId) {
      cache.invalidate({
        __typename: "Site",
        id: tenantId
      });
    } else {
      cache.invalidate({
        __typename: "Query",
        id: "sites"
      });
    }
  },
  createTenant: (result, args, cache) => {
    if (result.createTenant?.tenant) {
      // find all tenants query, and add it to the last page (i.e. the one with the highest "from" argument)

      const allQueriesSorted = cache
        .inspectFields("Query")
        .filter(({ fieldName }) => fieldName === "tenants")
        // make sure the arguments.from are sorted so we can grab the most recent one
        .sort((q1, q2) => {
          if (q1.arguments && q2.arguments) {
            return Number(q2.arguments.from) - Number(q1.arguments.from);
          }
          return 0;
        });

      const { id, name, type } = result.createTenant.tenant;

      // There many be many queries in the cache, so we need to find the highest "from" argument to get the last page of documents
      const highestFrom = allQueriesSorted[0].arguments?.from as number;

      // Filtering it down to the queries that actually need to be updated
      const queriesToUpdate = allQueriesSorted.filter(
        ({ arguments: args }) => args?.from === highestFrom
      );

      if (queriesToUpdate.length) {
        queriesToUpdate.forEach(field => {
          cache.updateQuery<AccountsListQuery, AccountsListQueryVariables>(
            {
              query: AccountsListDocument,
              variables: field.arguments as AccountsListQueryVariables
            },
            oldData => {
              if (!oldData) return null;
              // So that we don't mess with in-memory array reference, copy the array
              const copy = oldData.tenants.slice();
              copy.push({
                __typename: "Tenant",
                id,
                name,
                type: type ?? TenantType.Enterprise,
                sites: {
                  __typename: "SiteConnection",
                  count: 0
                },
                rooms: {
                  __typename: "RoomConnection",
                  count: 0
                },
                deviceCount: 0,
                memberCount: 0
              });
              return {
                __typename: "Query",
                tenants: copy
              };
            }
          );
        });
      }

      // Increase the tenantCount by 1
      cache.updateQuery<CountAccountsQuery>(
        {
          query: CountAccountsDocument
        },
        data => {
          if (data?.tenantCount) {
            return { __typename: "Query", tenantCount: data.tenantCount + 1 };
          }
          return data;
        }
      );
    }
  },
  deleteTenant: (result, args, cache) => {
    if (result.deleteTenant?.id) {
      const deletedId = result.deleteTenant.id;
      // Reduce the tenantCount by 1
      cache.updateQuery<CountAccountsQuery>(
        {
          query: CountAccountsDocument
        },
        data => {
          if (data?.tenantCount) {
            return { __typename: "Query", tenantCount: data.tenantCount - 1 };
          }
          return data;
        }
      );

      // Remove the deleted tenant from any tenants query
      const allQueries = cache
        .inspectFields("Query")
        .filter(({ fieldName }) => fieldName === "tenants");

      allQueries.forEach(({ arguments: queryArgs }) => {
        cache.updateQuery<AccountsListQuery, AccountsListQueryVariables>(
          {
            query: AccountsListDocument,
            variables: queryArgs as AccountsListQueryVariables
          },
          data => {
            if (!data) return null;
            // So that we don't mess with in-memory array reference, copy the array
            const copy = data.tenants.slice();
            const filtered = copy.filter(tenant => tenant.id !== deletedId);
            return {
              __typename: "Query",
              tenants: filtered
            };
          }
        );
      });
    }
  },
  createGroup: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "readAllGroups"
    });
  },
  deleteGroups: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "readAllGroups"
    });
  },
  updateGroup: (result, args, cache) => {
    if (args.groupId) {
      cache.invalidate({
        __typename: "GroupDto",
        id: args.groupId
      });
    }
  },
  updateDeviceGroupPriority: (result, args, cache) => {
    // invalidate getDeviceParametersExtended query for this device,
    // since parameters might be affected by group priority change
    invalidateDeviceParametersExtended(cache, {
      deviceId: args.serial
    });
  },
  importUserGroups: (result, args, cache) => {
    if (!result.importUserGroups) {
      return;
    }
    const { tenantId } = args;
    if (!tenantId) {
      return;
    }
    // invalidate getUserGroup results for this group
    cache.invalidate("Query", "userGroupsForTenant", {
      tenantId
    });
  },
  saveUserGroup: (result, args, cache) => {
    if (!result.saveUserGroup) {
      return;
    }
    const { tenantId } = args;
    if (!tenantId) {
      return;
    }
    // check for required fields to appease typescript
    const {
      id,
      name,
      updatedAt,
      createdAt,
      genericUsers,
      source
    } = result.saveUserGroup;
    if (
      !id ||
      !name ||
      !updatedAt ||
      !createdAt ||
      genericUsers === undefined ||
      !source
    ) {
      return;
    }
    // Maybe<T> doesn't like undefined
    const description = result.saveUserGroup.description ?? null;
    const policies = result.saveUserGroup.policies ?? null;

    // add/update saved group in listUserGroupsForTenant query
    const variables: UserGroupsForTenantQueryVariables = { tenantId };
    cache.updateQuery<UserGroupsForTenantQuery>(
      {
        query: UserGroupsForTenantDocument,
        variables
      },
      data => {
        // saveUserGroup upserts a user group, so we need to check for both cases (created or updated)
        // if we don't have a group with the returning id, push result
        if (!data?.userGroupsForTenant.find(group => group.id === id)) {
          data?.userGroupsForTenant.push({
            __typename: "UserGroup",
            id,
            name,
            source
          });
          return data;
        }
        // otherwise, find the updated group and apply changes
        const newGroupList: Array<{ __typename: "UserGroup" } & Pick<
          UserGroup,
          "id" | "name" | "source"
        >> = data.userGroupsForTenant.map(userGroup => {
          if (userGroup.id !== id) {
            return userGroup;
          }
          return { __typename: "UserGroup", id, name, source };
        });
        data.userGroupsForTenant = newGroupList;
        return data;
      }
    );
    cache.updateQuery<ListUserGroupsForTenantQuery>(
      {
        query: ListUserGroupsForTenantDocument,
        variables
      },
      data => {
        // saveUserGroup upserts a user group, so we need to check for both cases (created or updated)
        // if we don't have a group with the returning id, push result
        if (!data?.userGroupsForTenant.find(group => group.id === id)) {
          data?.userGroupsForTenant.push({
            __typename: "UserGroup",
            id,
            name,
            updatedAt,
            createdAt,
            genericUsers,
            source,
            description,
            policies
          });
          return data;
        }
        // otherwise, find the updated group and apply changes
        data.userGroupsForTenant.forEach((userGroup, index, arr) => {
          if (userGroup.id !== id) {
            return;
          }
          arr[index] = {
            __typename: "UserGroup",
            id,
            name,
            updatedAt,
            createdAt,
            genericUsers,
            source,
            description,
            policies
          };
        });
        return data;
      }
    );
    // invalidate getUserGroup results for this group
    cache.invalidate("Query", "getUserGroup", {
      id,
      tenantId
    });
  },
  deleteUserGroup: (result, args, cache) => {
    if (!result?.deleteUserGroup || result.deleteUserGroup.error) {
      return;
    }

    const { tenantId, id } = args;
    if (!tenantId || !id) {
      return;
    }
    // shared updater for list queries
    const deleteUpdater = <
      T extends ListUserGroupsForTenantQuery | UserGroupsForTenantQuery | null
    >(
      data: T
    ): T => {
      const spliceIndex =
        data?.userGroupsForTenant?.findIndex(group => group.id === id) ?? -1;
      if (spliceIndex > -1) {
        data?.userGroupsForTenant?.splice(spliceIndex, 1);
      }
      return data;
    };

    // remove deleted group from listUserGroupsForTenant query,
    // this query is used to populated the user group list page
    const variables: ListUserGroupsForTenantQueryVariables = { tenantId };
    cache.updateQuery<ListUserGroupsForTenantQuery>(
      {
        query: ListUserGroupsForTenantDocument,
        variables
      },
      deleteUpdater
    );
    // remove deleted group from listUserGroupsForTenant query,
    // this query is used to populate user group selection dropdowns
    cache.updateQuery<UserGroupsForTenantQuery>(
      {
        query: UserGroupsForTenantDocument,
        variables
      },
      deleteUpdater
    );
    // invalidate getUserGroup results for this group
    cache.invalidate("Query", "getUserGroup", {
      id,
      tenantId
    });
  },
  mutateUserGroupMembership: (result, args, cache) => {
    if (!result?.mutateUserGroupMembership) {
      return;
    }
    const { tenantId, groupId, newUsers, removedUsers } = args;
    if (
      !tenantId ||
      !groupId ||
      newUsers === undefined ||
      removedUsers === undefined
    ) {
      return;
    }
    // userGroupsForTenant only asks for userIds when resolving membership,
    // so we can update that field based on args from membership mutation
    const variables: ListUserGroupsForTenantQueryVariables = { tenantId };
    cache.updateQuery<ListUserGroupsForTenantQuery>(
      {
        query: ListUserGroupsForTenantDocument,
        variables
      },
      data => {
        if (!data?.userGroupsForTenant) {
          return data;
        }
        data.userGroupsForTenant.forEach((userGroup, index, arr) => {
          if (userGroup.id !== groupId) {
            return;
          }
          arr[index] = {
            ...userGroup,
            genericUsers:
              userGroup.genericUsers?.__typename === "GenericUserList"
                ? {
                    __typename: "GenericUserList",
                    items: userGroup.genericUsers.items
                      .filter(
                        user =>
                          !removedUsers
                            .concat(newUsers)
                            .includes(user?.user?.user?.user_id ?? "")
                      )
                      .concat(
                        newUsers.map(user_id => ({
                          __typename: "GenericUser",
                          user: {
                            __typename: "UserResponse",
                            user: {
                              __typename: "User",
                              user_id: user_id
                            }
                          }
                        }))
                      )
                  }
                : userGroup.genericUsers
          };
        });
        return data;
      }
    );

    // invalidate getUserGroup results for this group
    cache.invalidate("Query", "getUserGroup", {
      id: groupId,
      tenantId
    });
  },
  activateLicense: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "licenses"
    });
    cache.invalidate({
      __typename: "Query",
      id: "licenseCount"
    });
  },
  generateLicense: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "licenses"
    });
    cache.invalidate({
      __typename: "Query",
      id: "licenseCount"
    });
  },
  deleteMockRecords: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "licenses"
    });
    cache.invalidate({
      __typename: "Query",
      id: "licenseCount"
    });
  },
  entitleDevices: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "licenses"
    });
  },
  entitleAllDevicesForModel: (result, args, cache) => {
    cache.invalidate({
      __typename: "Query",
      id: "licenses"
    });
  },
  ncEnableUser(result, args, cache) {
    const input = args.input;
    if (!input || !input.user_id || !input.tenantId) return;
    cache.updateQuery<AudienceForUserQuery, AudienceForUserQueryVariables>(
      {
        query: AudienceForUserDocument,
        variables: {
          user_id: input.user_id,
          tid: input.tenantId
        }
      },
      old => {
        if (!old) return null;
        const userAudience = result.ncEnableUser as Audience;
        return {
          ...old,
          ncPoly: {
            ...old.ncPoly,
            userAudience: {
              ...userAudience,
              __typename: "Audience"
            }
          }
        };
      }
    );
  },
  ncAddRecipient: (result, args, cache) => {
    const to = args.input?.audienceId!;
    const existing = cache.readFragment<Audience>(CommonAudienceFragmentDoc, {
      __typename: "Audience",
      id: to
    });
    if (existing) {
      //@ts-ignore
      existing.recipients.push(result);
      cache.writeFragment(CommonAudienceFragmentDoc, existing);
    }
  },
  ncEnableTenant(result, args, cache) {
    const input = args.id;
    if (!input) return;
    cache.updateQuery<AudienceForTenantQuery, AudienceForTenantQueryVariables>(
      {
        query: AudienceForTenantDocument,
        variables: {
          tenantId: input
        }
      },
      old => {
        if (!old) return null;
        const audience = result.ncEnableTenant as Audience;
        return {
          ...old,
          tenant: {
            ...old.tenant,
            audience: {
              ...audience,
              __typename: "Audience"
            }
          }
        };
      }
    );
  },
  ncCreateTeamsWebhookForTenant(result, args, cache) {
    const input = args.tenantId;
    if (!input) return;
    const id = result.ncCreateTeamsWebhookForTenant?.id;
    if (!id) return;
    cache.updateQuery<
      TeamsHooksForTenantQuery,
      TeamsHooksForTenantQueryVariables
    >(
      {
        query: TeamsHooksForTenantDocument,
        variables: {
          id: input
        }
      },
      old => {
        if (!old) return null;
        old.tenant.teamsWebhooks?.push(
          result.ncCreateTeamsWebhookForTenant as any
        );
        return old;
      }
    );
  },
  ncDeleteTeamsWebhook(result, args, cache) {
    const input = args.id;
    if (!input) return;
    cache.invalidate({
      __typename: "TeamsHook",
      id: input
    });
  },
  ncCreateServiceNowWebhook(result, args, cache) {
    const input = args.tenantId;
    if (!input) return;
    const id = result.ncCreateServiceNowWebhook?.id;
    if (!id) return;
    cache.updateQuery<
      ServiceNowHooksForTenantQuery,
      ServiceNowHooksForTenantQueryVariables
    >(
      {
        query: ServiceNowHooksForTenantDocument,
        variables: {
          id: input
        }
      },
      old => {
        if (!old) return null;
        old.tenant.serviceNowWebhooks?.push(
          result.ncCreateServiceNowWebhook as any
        );
        return old;
      }
    );
  },
  ncDeleteServiceNowWebhook(result, args, cache) {
    const input = args.id;
    if (!input) return;
    cache.invalidate({
      __typename: "ServiceNowWebhook",
      id: input
    });
  },
  importToLens: (result, args, cache) => {
    if (result.importToLens) {
      cache
        .inspectFields("Query")
        .filter(
          ({ fieldName, fieldKey }) =>
            ["devices", "deviceAggregations", "countDevices"].includes(
              fieldName
            ) &&
            (!args.tenantId || fieldKey.includes(args.tenantId))
        )
        .forEach(({ fieldKey }) => {
          cache.invalidate("Query", fieldKey);
        });
    }
  },
  // License Server Client Auth Tokens
  ns_ls_createAuthToken: (result, args, cache) => {
    invalidateQueries(cache, "ns_ls_searchClientAuthTokens");
  },
  ns_ls_deleteAuthToken: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_ClientAuthToken",
        recordId: args.recordId
      });
    }
  },
  // License Server Entitlements
  ns_ls_addEntitlement: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_updateEntitlement: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_assignEntitlement: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_combineEntitlements: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Entitlement",
        recordId: args.recordId
      });
    }
    if (args.toRecordId) {
      cache.invalidate({
        __typename: "ns_ls_Entitlement",
        recordId: args.toRecordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_deleteEntitlement: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Entitlement",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_splitEntitlement: (result, args, cache) => {
    if (args.existingEntitlementRecordId) {
      cache.invalidate({
        __typename: "ns_ls_Entitlement",
        recordId: args.existingEntitlementRecordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_unassignEntitlement: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_releaseEntitlement: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_searchDeleteEntitlements: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_assignEntitlementValue: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  ns_ls_releaseEntitlementValue: (result, args, cache) => {
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_getInstance",
      "ns_ls_searchInstances"
    );
  },
  // License Server Instances
  ns_ls_createAltInstance: (result, args, cache) => {
    invalidateQueries(cache, "ns_ls_searchInstances");
  },
  ns_ls_createInstance: (result, args, cache) => {
    invalidateQueries(cache, "ns_ls_searchInstances");
  },
  ns_ls_deleteInstance: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Instance",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView"
    );
  },
  ns_ls_releaseInstance: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Instance",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView"
    );
  },
  ns_ls_processLicenseRequest: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Instance",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView"
    );
  },
  ns_ls_generateAlternateLicense: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Instance",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView"
    );
  },
  ns_ls_ackInstance: (result, args, cache) => {
    if (args.recordId) {
      cache.invalidate({
        __typename: "ns_ls_Instance",
        recordId: args.recordId
      });
    }
    invalidateQueries(
      cache,
      "ns_ls_getEntitlement",
      "ns_ls_searchEntitlements",
      "ns_ls_searchEntitlementGroupingSets",
      "ns_ls_getEntitlementsByGroupingView"
    );
  },
  addResourceFile: (result, args, cache) => {
    invalidateQueries(cache, "getAllResourceFiles");
  },
  deleteResourceFiles: (result, args, cache) => {
    invalidateQueries(cache, "getAllResourceFiles");
  },
  // Clariti Trial Subscriptions
  createClaritiTrialSubscription: (result, args, cache) => {
    invalidateQueries(cache, "getClaritiTrialSubscriptionReports");
  },
  deleteClaritiTrialSubscriptionReport: (result, args, cache) => {
    if (args.id) {
      cache.invalidate({
        __typename: "ClaritiTrialSubscriptionReport",
        id: args.id
      });
    }
    invalidateQueries(cache, "getClaritiTrialSubscription");
  }
};

export default Mutation as UpdatesConfig["Mutation"];
